import { memo, useEffect, useRef, useState, type ReactNode } from 'react';
import { mountBackdrop, type Scene } from './backdropPainters.js';

/** Effective theme: <html data-theme> when set, else the OS preference. */
function currentScene(): Scene {
  const t = document.documentElement.getAttribute('data-theme');
  if (t === 'light') return 'day';
  if (t === 'dark') return 'night';
  return matchMedia('(prefers-color-scheme: light)').matches ? 'day' : 'night';
}

/**
 * Full-viewport app backdrop: woodland dapple by day, drifting night mist by night,
 * cross-fading when the theme flips. Rendered once behind the app frame.
 */
export const Backdrop = memo(function Backdrop(): ReactNode {
  const ref = useRef<HTMLDivElement>(null);
  // Initial scene only; after mount the controller owns data-scene.
  const [initial] = useState(currentScene);
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const ctl = mountBackdrop(root, initial);
    const sync = (): void => ctl.setScene(currentScene());
    const mo = new MutationObserver(sync);
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    const mq = matchMedia('(prefers-color-scheme: light)');
    mq.addEventListener('change', sync);
    sync(); // in case the theme flipped between render and mount
    return () => {
      mo.disconnect();
      mq.removeEventListener('change', sync);
      ctl.destroy();
    };
  }, [initial]);
  return (
    <div ref={ref} className="ambient" aria-hidden="true" data-scene={initial}>
      <div className="ambient-layer ambient-day">
        <canvas />
      </div>
      <div className="ambient-layer ambient-night">
        <canvas />
      </div>
    </div>
  );
});
